import { useMemo, useRef } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { useDateSort } from '../../../hooks/useDateSort';
import TableActionsMenu from '../../common/TableActionsMenu';
import { NewsMagazineStats } from '../../../types/naver.types';
import type { Dayjs } from 'dayjs';

type Props = {
  data: NewsMagazineStats[];
  startDate: Dayjs;
  endDate: Dayjs;
};

type CpTypeMetrics = {
  click: number;
  postbackSuccess: number;
};

type PivotRow = {
  date: string;
  [cpType: string]: CpTypeMetrics | string;
};

const formatNumber = (value: number) => value.toLocaleString('ko-KR');

const formatRate = (click: number, postbackSuccess: number) =>
  click ? `${((postbackSuccess / click) * 100).toFixed(1)}%` : '-';

export default function NewsMagazineStatsTable({ data, startDate, endDate }: Props) {
  const tableRef = useRef<HTMLTableElement>(null);

  const filename = useMemo(
    () => `news_magazine_stats_${startDate.format('YYMMDD')}-${endDate.format('YYMMDD')}`,
    [startDate, endDate]
  );

  const cpTypes = useMemo(() => {
    const seen = new Set<string>();
    data.forEach((row) => seen.add(row.cpType));
    return Array.from(seen).sort();
  }, [data]);

  const pivotData = useMemo<PivotRow[]>(() => {
    const map = new Map<string, PivotRow>();
    data.forEach((row) => {
      let entry = map.get(row.date);
      if (!entry) {
        entry = { date: row.date };
        map.set(row.date, entry);
      }
      const prev = (entry[row.cpType] as CpTypeMetrics | undefined) ?? { click: 0, postbackSuccess: 0 };
      entry[row.cpType] = {
        click: prev.click + row.click,
        postbackSuccess: prev.postbackSuccess + row.postbackSuccess,
      };
    });
    return Array.from(map.values());
  }, [data]);

  const { sorted, sortDirection, toggleSort } = useDateSort(pivotData);

  const cellSx = { borderLeft: '1px solid #e2e8f0' };

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <TableActionsMenu tableRef={tableRef} filename={filename} />
      </Box>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table ref={tableRef} size="small" sx={{ whiteSpace: 'nowrap' }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f1f5f9' }}>
              <TableCell rowSpan={2} sx={{ verticalAlign: 'middle', fontWeight: 700 }}>
                <TableSortLabel active direction={sortDirection} onClick={toggleSort}>
                  날짜
                </TableSortLabel>
              </TableCell>
              {cpTypes.map((cp) => (
                <TableCell key={cp} colSpan={3} align="center" sx={{ ...cellSx, fontWeight: 700 }}>
                  {cp}
                </TableCell>
              ))}
            </TableRow>
            <TableRow sx={{ backgroundColor: '#f8fafc' }}>
              {cpTypes.map((cp) => (
                <>
                  <TableCell key={`${cp}-click`} align="right" sx={cellSx}>
                    클릭수
                  </TableCell>
                  <TableCell key={`${cp}-postbackSuccess`} align="right">
                    포스트백 성공
                  </TableCell>
                  <TableCell key={`${cp}-cvr`} align="right">
                    CVR
                  </TableCell>
                </>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((row) => (
              <TableRow key={row.date} sx={{ '&:hover': { backgroundColor: '#f8fafc' } }}>
                <TableCell>{row.date}</TableCell>
                {cpTypes.map((cp) => {
                  const metrics = row[cp] as CpTypeMetrics | undefined;
                  return (
                    <>
                      <TableCell key={`${cp}-click`} align="right" sx={cellSx}>
                        {metrics ? formatNumber(metrics.click) : '-'}
                      </TableCell>
                      <TableCell key={`${cp}-postbackSuccess`} align="right">
                        {metrics ? formatNumber(metrics.postbackSuccess) : '-'}
                      </TableCell>
                      <TableCell key={`${cp}-cvr`} align="right" sx={{ fontWeight: 700 }}>
                        {metrics ? formatRate(metrics.click, metrics.postbackSuccess) : '-'}
                      </TableCell>
                    </>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
